import { useLanguage } from "../context/LanguageContext";
import { motion } from "framer-motion";
import { ArrowRight, ArrowDown, Check, X } from "lucide-react";

export const WhatIsSection = () => {
  const { t, language } = useLanguage();

  const steps = [
    {
      number: "01",
      title: t("whatIs.step1.title"),
      desc: t("whatIs.step1.desc"),
      color: "bg-red-50 border-red-200",
      textColor: "text-red-600",
      darkColor: "dark:bg-red-900/20 dark:border-red-700/40 dark:text-red-400",
    },
    {
      number: "02",
      title: t("whatIs.step2.title"),
      desc: t("whatIs.step2.desc"),
      color: "bg-amber-50 border-amber-200",
      textColor: "text-amber-600",
      darkColor:
        "dark:bg-amber-900/20 dark:border-amber-700/40 dark:text-amber-400",
    },
    {
      number: "03",
      title: t("whatIs.step3.title"),
      desc: t("whatIs.step3.desc"),
      color: "bg-emerald-50 border-emerald-200",
      textColor: "text-emerald-600",
      darkColor:
        "dark:bg-emerald-900/20 dark:border-emerald-700/40 dark:text-emerald-400",
    },
  ];

  const comparisonLead =
    language === "fr"
      ? "Chez Éco-Bain, le réémaillage de baignoire débute à 385 $. L’intervention se fait sur place en 90 minutes, sans démolition, et la baignoire est utilisable dès le lendemain."
      : "At Éco-Bain, bathtub reglazing starts at $385. The work is completed on site in 90 minutes, with no demolition, and the bathtub can be used the next day.";

  return (
    <section
      id="service"
      className="
        section-padding
        bg-white
        dark:bg-[#141617]
        transition-colors duration-300
      "
    >
      <div className="container-width">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <span className="text-[#5cb85c] font-semibold text-sm tracking-wider mb-3 block">
            {t("whatIs.sectionTag")}
          </span>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 dark:text-white mb-6 transition-colors">
            {t("whatIs.title")}
          </h2>

          <p className="text-lg text-slate-600 dark:text-slate-300 transition-colors">
            {t("whatIs.description")}
          </p>
        </motion.div>

        {/* Steps */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="grid md:grid-cols-3 gap-3 md:gap-6 mb-12 md:mb-20"
        >
          {steps.map((step, index) => (
            <div key={index} className="flex flex-col items-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className={`
                  relative w-full p-6 rounded-2xl border-2
                  ${step.color}
                  ${step.darkColor}
                  transition-colors duration-300
                `}
              >
                <span
                  className={`
                    text-5xl font-bold opacity-20 absolute top-4 right-4
                    ${step.textColor}
                  `}
                >
                  {step.number}
                </span>

                <div className="relative z-10">
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-3 transition-colors">
                    {step.title}
                  </h3>
                  <p className="text-slate-600 dark:text-slate-300 transition-colors">
                    {step.desc}
                  </p>
                </div>

                {/* Desktop arrow */}
                {index < steps.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 -translate-y-1/2 z-20">
                    <ArrowRight
                      className="text-slate-300 dark:text-white/20"
                      size={24}
                    />
                  </div>
                )}
              </motion.div>

              {/* Mobile arrow */}
              {index < steps.length - 1 && (
                <div className="md:hidden my-2 flex justify-center">
                  <ArrowDown
                    className="text-slate-300 dark:text-white/20"
                    size={16}
                  />
                </div>
              )}
            </div>
          ))}
        </motion.div>

        {/* Comparison */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto"
        >
          <h3 className="text-2xl font-bold text-slate-900 dark:text-white text-center mb-4 transition-colors">
            {t("whatIs.comparison.title")}
          </h3>

          <p className="text-center text-slate-600 dark:text-slate-300 max-w-3xl mx-auto mb-10 transition-colors">
            {comparisonLead}
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Replace */}
            <div className="bg-slate-50 dark:bg-black/30 rounded-2xl p-6 border-2 border-slate-200 dark:border-white/10 transition-colors">
              <h4 className="text-xl font-bold text-slate-700 dark:text-slate-200 mb-4 flex items-center gap-2">
                <X className="text-red-500" size={24} />
                {t("whatIs.comparison.replace.title")}
              </h4>

              <ul className="space-y-3">
                {t("whatIs.comparison.replace.items").map((item, i) => (
                  <li
                    key={i}
                    className="flex gap-3 text-slate-600 dark:text-slate-300"
                  >
                    <X className="text-red-400 mt-1" size={16} />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Restore */}
            <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-2xl p-6 border-2 border-[#5cb85c] transition-colors">
              <h4 className="text-xl font-bold text-[#5cb85c] mb-4 flex items-center gap-2">
                <Check size={24} />
                {t("whatIs.comparison.restore.title")}
              </h4>

              <ul className="space-y-3">
                {t("whatIs.comparison.restore.items").map((item, i) => (
                  <li
                    key={i}
                    className="flex gap-3 text-slate-700 dark:text-slate-200"
                  >
                    <Check className="mt-1" size={16} />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
